// Radha — shared Supabase login card for the direct-path modules. This is the
// app's actual front door (a direct-to-login SPA with no separate marketing
// page), so it carries the hero treatment: headline, one line of supporting
// copy, one primary action — same sign-in logic as before, tenant resolution
// unchanged.
import React, { useState } from "react";

import { signIn } from "../lib/authSupabase";
import { TempleArch, YamunaDivider, SanctuaryParticles, SanctuaryOrbs } from "./DevotionalMotifs";
import { CsmLogo } from "./CsmLogo";

const fld: React.CSSProperties = {
  padding: "11px 13px", border: "1px solid var(--border)", borderRadius: 10, fontSize: 14,
  background: "var(--surface)", color: "var(--fg)", width: "100%", boxSizing: "border-box",
};

export default function SupabaseLoginCard({ title }: { title: string }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(email.trim(), password);
    } catch (err) {
      setError(err instanceof Error ? err.message : "sign in failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", position: "relative", padding: "24px 16px" }}>
      <SanctuaryOrbs />
      <SanctuaryParticles />
      <form onSubmit={submit} className="sanctuary-card sanctuary-card-interactive sanctuary-reveal"
        style={{
          maxWidth: 380, width: "100%", padding: "40px 32px 32px", display: "grid", gap: 14,
          position: "relative", overflow: "hidden", zIndex: 1,
        }}>
        <TempleArch width={340} height={140} className="font-devotional-display"
          style={{ position: "absolute", top: -6, left: "50%", transform: "translateX(-50%)", color: "var(--accent-gold)", opacity: .5, pointerEvents: "none" }} />

        <div style={{ position: "relative", display: "flex", justifyContent: "center", margin: "30px 0 10px" }}>
          <CsmLogo size={64} alt="Chandrika Seva Mission" />
        </div>

        <h1 className="font-devotional-display sanctuary-glow-text" style={{
          margin: 0, textAlign: "center", fontSize: 28, letterSpacing: ".02em",
          background: "linear-gradient(120deg, var(--accent-gold), var(--accent-pink), var(--accent-purple))",
          WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent",
        }}>
          {title === "Radha" ? "Radha Rasvyn" : title}
        </h1>
        <p className="font-devotional-copy" style={{ color: "var(--fg-muted)", fontSize: 15.5, margin: "0 4px", textAlign: "center", lineHeight: 1.5 }}>
          Grace and clarity for every ledger. Sign in to your sanctuary of receivables.
        </p>

        <YamunaDivider className="font-devotional-display" style={{ color: "var(--accent-purple)", opacity: .5, margin: "4px 0" }} />

        <label style={{ display: "grid", gap: 5, fontSize: 12.5, color: "var(--fg-muted)" }}>
          Work email
          <input
            type="email" placeholder="you@company.com" value={email} autoComplete="username"
            onChange={(e) => setEmail(e.target.value)}
            style={fld}
          />
        </label>
        <label style={{ display: "grid", gap: 5, fontSize: 12.5, color: "var(--fg-muted)" }}>
          Password
          <input
            type="password" placeholder="••••••••" value={password} autoComplete="current-password"
            onChange={(e) => setPassword(e.target.value)}
            style={fld}
          />
        </label>
        <button type="submit" disabled={busy} className="sanctuary-btn-primary"
          style={{ padding: 12, borderRadius: 10, cursor: busy ? "default" : "pointer", fontSize: 14.5, marginTop: 4 }}>
          {busy ? "Signing in…" : "Enter Radha Rasvyn"}
        </button>
        {error && <div style={{ color: "var(--status-critical)", fontSize: 13, textAlign: "center" }}>{error}</div>}

        <p style={{ color: "var(--fg-faint)", fontSize: 11.5, textAlign: "center", margin: "2px 0 0" }}>
          Your data is scoped to your tenant by the database — nothing crosses beyond your sign-in.
        </p>
      </form>
    </div>
  );
}
